<template>
  <div>
    <Notification />
    <div class='header'>
      <h3><span v-if='!loading || count'>{{ count }} Subscriptions</span></h3>
      <div class='actions'>
        <div>
          <span class="switch-label">Show active subscriptions</span>
          <ToggleSwitch :onChange='toggleActivePlans' v-model='activePlan'></ToggleSwitch>
        </div>
        <Dropdown :onChange='filterPlan' v-model="plan" width='175px'>
          <a-select-option title="Plans: All" value="all">All</a-select-option>
          <a-select-option title="Plans: Customer" value="customer">Customer</a-select-option>
          <a-select-option title="Plans: Trial" value="trial">Trial</a-select-option>
        </Dropdown>
        <button class='flat-button new-subscription' @click='openForm'>New Subscription</button>

        <a-modal class="subscription-modal" :destroyOnClose='true' :footer='null' :width='600' :closable='false' v-model="formOpened" @cancel='closeForm'>
          <NewSubscriptionForm />
        </a-modal>

        <a-modal title="Hang on, are you sure?" class="delete-subscription-modal" :destroyOnClose='true' :footer='null' :width='600' :closable='false' v-model="openDeleteSubscriptionModal" @cancel='cancelDeleteSubscription'>
          <ConfirmDeleteSubscription />
        </a-modal>

      </div>
    </div>

    <Table class='subscription-table'
      :data="subscriptions"
      :totalRows='count'
      :pagination='{ current: page }'
      :onChange='handleTableChange'
      :loading='loading'>
      <a-table-column dataIndex="email" key="name">
        <span class="search-cell" slot="title">
          <img src="../assets/search.png" class='search-icon' @click='showSearch'>
          <SearchInput v-if="showSearchInput" @searched='search' @hideSearchInput='hideSearch' />
          <span v-if='!showSearchInput'>Subscription</span>
        </span>
        <template slot-scope="name, record">
          <div class='clickable-cell' @click.prevent='() => editSubscription(record.id)'>
            <a>{{ generateName()(record) }}</a>
          </div>
        </template>
      </a-table-column>
      <a-table-column title='Plan' dataIndex='plan' key='plan' :sorter='true'>
        <template slot-scope="_name, record">
          <PlanDetails :name='record.plan' :expiresAt='record.expiresAt'></PlanDetails>
        </template>
      </a-table-column>
      <a-table-column title='Start date' dataIndex='createdAt' key='createdAt' :sorter='true' :sortDirections='["descend", "ascend"]'>
        <template slot-scope="createdAt">{{ prettyDate(createdAt) }}</template>
      </a-table-column>
      <a-table-column title='End date' dataIndex='expiresAt' key='expiresAt' :sorter='true' :sortDirections='["descend", "ascend"]'>
        <template slot-scope="expiresAt">{{ prettyDate(expiresAt) }}</template>
      </a-table-column>
      <a-table-column title='Total users' dataIndex='subscriptionHolders' key='subscriptionHolders' :sorter='true'>
        <template slot-scope="subscriptionHolders">{{ totalUsers(subscriptionHolders) }}</template>
      </a-table-column>
      <a-table-column :width='43' dataIndex='id' key='id' fixed='right'>
        <template slot-scope='id'>
          <a-popover trigger="click">
            <template slot="content">
              <a @click='openConfirmDelete(id)'>Delete</a>
            </template>
            <span class='clickable-cell'>
              <MoreDotsIcon class="more-dots" />
            </span>
          </a-popover>
        </template>
      </a-table-column>
    </Table>
  </div>
</template>

<script>
import Notification from '@/components/Notification'
import Table from '@/components/Table'
import PlanDetails from '@/components/PlanDetails'
import Dropdown from '@/components/Dropdown'
import ToggleSwitch from '@/components/ToggleSwitch'
import NewSubscriptionForm from '@/components/subscriptions/NewSubscriptionForm'
import MoreDotsIcon from '../../public/more-dots-icon.svg'
import ConfirmDeleteSubscription from '@/components/subscriptions/ConfirmDeleteSubscription'
import SearchInput from '@/components/SearchInput'

import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import parseDomain from 'parse-domain'
import dayjs from 'dayjs'
import * as moment from 'moment'

export default {
  name: 'subscriptions',
  components: {
    Table,
    PlanDetails,
    Dropdown,
    NewSubscriptionForm,
    Notification,
    MoreDotsIcon,
    ConfirmDeleteSubscription,
    SearchInput,
    ToggleSwitch
  },
  data() {
    return {
      today: moment(),
      plan: 'all',
      showSearchInput: false,
      activePlan: false
    }
  },
  methods: {
    ...mapActions('subscriptions', ['fetchPage']),
    ...mapMutations('newSubscriptionForm', { openForm: 'open', closeForm: 'close', updateForm: 'update', addCompany: 'addCompany' }),
    ...mapMutations('subscriptions', ['updateDeleteSubscription', 'cancelDeleteSubscription']),
    ...mapGetters('subscriptions', ['getSubscription', 'generateName']),
    totalUsers(users) {
      return users ? users.split(',').length : 0
    },
    prettyDate(date) {
      return dayjs(date).format('D MMMM, YYYY')
    },
    handleTableChange(pagination, filters, sorter) {
      this.fetchPage({
        page: pagination.current,
        order: sorter.field,
        asc: sorter.order === 'ascend',
      })
    },
    filterPlan(plan) {
      if(plan === 'all') plan = null
      this.fetchPage({plan, active: this.activePlan})
    },
    toggleActivePlans(active) {
      let plan = (this.plan === 'all' ? null : this.plan)
      this.fetchPage({active, plan})
    },
    openConfirmDelete(id) {
      this.updateDeleteSubscription(id)
    },
    editSubscription(id) {
      const form = this.getSubscription()(id)

      this.addCompany({id: form.companyId, name: form.companyName, registrationId: form.companyRegistrationId})

      const users = form.subscriptionHolders ? form.subscriptionHolders.split(',').map(email => ({ email, password: null })) : []
      const editedForm = Object.assign({}, form, { users, country: form.country || '' })

      this.updateForm(editedForm)

      this.openForm()
    },
    showSearch() {
      this.showSearchInput = true
    },
    hideSearch() {
      this.showSearchInput = false
    },
    search(searchTerm) {
      this.fetchPage({ search: searchTerm })
    }
  },
  mounted() {
    this.fetchPage()
  },
  computed: {
    ...mapState('subscriptions', ['subscriptions', 'count', 'page', 'loading', 'order', 'asc', 'deleteSubscriptionId']),
    ...mapState('newSubscriptionForm', { formOpened: 'opened' }),
    openDeleteSubscriptionModal() {
      return this.deleteSubscriptionId ? true : false
    }
  }
}
</script>

<style scoped lang='stylus'>
@import '../styles/buttons'
@import '../styles/forms'

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    margin: 0;
  }

  .new-subscription {
    margin-left: $spacingMd;
  }

  .actions {
    display: flex;
    align-items: center;
    .ant-switch {
      margin-right: 20px;
    }
    .switch-label {
      font-weight: 500;
      margin-right: 5px;
    }
  }
}

.subscription-table {
  margin-top: $spacingLg;
}

</style>

<style lang='stylus'>
  
  .subscription-modal {
    .ant-modal-body {
      margin-top: 0px;
    }
  }
  
  .subscription-table {
    .ant-table-row > td {
      padding: 0px;
      padding-left: 20px;
    }
    
    .clickable-cell {
      cursor: pointer;
      &:hover a {
        color: $primaryBlue;
      }
    }
    
    .search-icon {
      width: 20px;
      height: 19px;
      cursor: pointer;
      margin-right: 10px;
    }
    
    .search-cell {
      display: flex;
    }
  }

</style>